/**
 * premiumTokens.js
 * Design System Premium - Tokens de Fundação v2.0
 * Suporte a Dark/Light Mode com Glassmorphism e Accent Teal/Cyan
 * 
 * VERSÃO 2.0 - Expandida com:
 * - Tokens de botões (button)
 * - Status de viagem/trip (tripStatus)
 * - Tokens de charts/gráficos
 * - Tokens de Command Bar
 * - Tokens de Activity Feed
 * 
 * Inspirado em: Linear App, Fleet Management Community, Samsara, GitHub
 */

// ============================================================================
// TOKENS COMPARTILHADOS (mode-agnostic)
// ============================================================================

export const typography = {
  fontFamily: {
    heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", Consolas, monospace',
  },
  fontSize: {
    xs: '0.6875rem',   // 11px - micro labels
    sm: '0.75rem',     // 12px - labels pequenos
    md: '0.875rem',    // 14px - texto padrão
    lg: '1rem',        // 16px - texto destacado
    xl: '1.125rem',    // 18px - subtítulos
    '2xl': '1.25rem',  // 20px - títulos pequenos
    '3xl': '1.5rem',   // 24px - títulos
    '4xl': '2rem',     // 32px - títulos grandes
    '5xl': '2.5rem',   // 40px - KPIs
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 1.75,
  },
  letterSpacing: {
    tighter: '-0.03em',
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
    wider: '0.05em',   // Para labels uppercase
    widest: '0.1em',
  },
};

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  11: '2.75rem',    // 44px
  12: '3rem',       // 48px
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
};

export const radius = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '20px',
  '4xl': '24px',
  full: '9999px',
};

export const transitions = {
  duration: {
    instant: '50ms',
    fast: '100ms',
    normal: '150ms',
    moderate: '200ms',
    slow: '300ms',
    slower: '400ms',
    slowest: '500ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
  // Presets prontos para uso
  all: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  allSlow: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  colors: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
  opacity: 'opacity 150ms ease',
  shadow: 'box-shadow 150ms ease',
  width: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  height: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export const zIndex = {
  hide: -1,
  base: 0,
  docked: 10,
  dropdown: 100,
  sticky: 200,
  banner: 250,
  fixed: 300,
  modalBackdrop: 350,
  modal: 400,
  popover: 500,
  tooltip: 600,
  toast: 700,
  commandBar: 800,
  maximum: 9999,
};

// Breakpoints para media queries
export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
  '2xl': '1920px',
};

// ============================================================================
// DARK MODE TOKENS
// ============================================================================

export const darkTokens = {
  // Accent colors - Teal/Cyan premium (identidade da marca)
  accent: {
    primary: '#00d4aa',
    primaryHover: '#00e4ba',
    primaryActive: '#00c49a',
    secondary: '#00b8d4',
    tertiary: '#00e5ff',
    glow: 'rgba(0, 212, 170, 0.4)',
    subtle: 'rgba(0, 212, 170, 0.1)',
    hover: 'rgba(0, 212, 170, 0.15)',
    muted: 'rgba(0, 212, 170, 0.6)',
  },

  // Surfaces - GitHub Dark inspired (melhorado para visibilidade)
  surface: {
    base: '#0d1117',
    elevated: '#161b22',
    sunken: '#010409',
    overlay: 'rgba(22, 27, 34, 0.9)',
    overlayStrong: 'rgba(0, 0, 0, 0.7)',
    glass: 'rgba(255, 255, 255, 0.08)',        // Aumentado de 0.05
    glassHover: 'rgba(255, 255, 255, 0.14)',   // Aumentado de 0.08
    glassBorder: 'rgba(255, 255, 255, 0.12)',  // Aumentado de 0.08
    // Surfaces específicas
    card: '#161b22',
    cardHover: '#1c2128',
    cardActive: '#21262d',
    input: '#0d1117',
    inputHover: '#161b22',
    inputFocus: '#0d1117',
    hover: '#1f2937',                          // Mais visível
    active: '#21262d',
    selected: 'rgba(0, 212, 170, 0.12)',       // Aumentado de 0.08
  },

  // Status colors - Padrão Traccar + Moving azul (customização 2ECOMP)
  status: {
    online: '#4caf50',            // Verde (padrão Traccar)
    offline: '#f44336',           // Vermelho (padrão Traccar)
    moving: '#3b82f6',            // Azul (customização 2ECOMP)
    idle: '#f59e0b',              // Âmbar - parado com ignição
    stopped: '#f59e0b',           // Âmbar (igual idle)
    alarm: '#ef4444',             // Vermelho alarme
    maintenance: '#a371f7',
    unknown: '#9e9e9e',           // Cinza (padrão Traccar)
    // Glows para destaque
    onlineGlow: 'rgba(76, 175, 80, 0.4)',
    offlineGlow: 'rgba(244, 67, 54, 0.4)',
    movingGlow: 'rgba(59, 130, 246, 0.5)',
    alarmGlow: 'rgba(239, 68, 68, 0.5)',
    // Backgrounds para badges
    onlineBg: 'rgba(76, 175, 80, 0.15)',
    offlineBg: 'rgba(244, 67, 54, 0.15)',
    movingBg: 'rgba(59, 130, 246, 0.15)',
    idleBg: 'rgba(245, 158, 11, 0.15)',
    stoppedBg: 'rgba(245, 158, 11, 0.15)',
    alarmBg: 'rgba(239, 68, 68, 0.15)',
    maintenanceBg: 'rgba(163, 113, 247, 0.15)',
  },

  // Trip/Dispatch Status (Fleet Management inspired)
  tripStatus: {
    planned: '#8b949e',
    scheduled: '#a371f7',
    inProgress: '#58a6ff',
    loading: '#d29922',
    unloading: '#bf8700',
    waitingToLoad: '#e3b341',
    waitingToUnload: '#9a6700',
    completed: '#3fb950',
    delayed: '#f97316',
    cancelled: '#f85149',
    onTime: '#00d4aa',
    early: '#00b8d4',
    late: '#f97316',
    // Backgrounds
    plannedBg: 'rgba(139, 148, 158, 0.15)',
    scheduledBg: 'rgba(163, 113, 247, 0.15)',
    inProgressBg: 'rgba(88, 166, 255, 0.15)',
    completedBg: 'rgba(63, 185, 80, 0.15)',
    delayedBg: 'rgba(249, 115, 22, 0.15)',
    cancelledBg: 'rgba(248, 81, 73, 0.15)',
  },

  // Semantic colors - General UI use
  semantic: {
    success: '#00d4aa',
    successHover: '#00e4ba',
    error: '#f85149',
    errorHover: '#ff6b5f',
    warning: '#d29922',
    warningHover: '#e2a932',
    info: '#58a6ff',
    infoHover: '#68b6ff',
    neutral: '#6e7681',
    purple: '#a371f7',
    // Backgrounds semânticos
    successBg: 'rgba(0, 212, 170, 0.1)',
    errorBg: 'rgba(248, 81, 73, 0.1)',
    warningBg: 'rgba(210, 153, 34, 0.1)',
    infoBg: 'rgba(88, 166, 255, 0.1)',
    neutralBg: 'rgba(110, 118, 129, 0.1)',
    purpleBg: 'rgba(163, 113, 247, 0.1)',
  },

  // Text hierarchy (melhorado para visibilidade)
  text: {
    primary: '#f1f5f9',        // Slate 100 - mais brilhante
    secondary: '#94a3b8',      // Slate 400 - mais visível que #8b949e
    tertiary: '#64748b',       // Slate 500
    muted: '#64748b',          // Slate 500
    disabled: '#475569',       // Slate 600
    placeholder: '#64748b',    // Slate 500
    accent: '#00d4aa',
    inverse: '#0d1117',
    onAccent: '#ffffff',
    link: '#58a6ff',
    linkHover: '#79b8ff',
    error: '#f85149',
    success: '#00d4aa',
    warning: '#d29922',
  },

  // Borders (melhorado para visibilidade)
  border: {
    subtle: 'rgba(255, 255, 255, 0.08)',    // Aumentado de 0.04
    default: 'rgba(255, 255, 255, 0.12)',   // Aumentado de 0.08
    strong: 'rgba(255, 255, 255, 0.2)',     // Aumentado de 0.16
    accent: 'rgba(0, 212, 170, 0.35)',      // Aumentado de 0.3
    accentStrong: 'rgba(0, 212, 170, 0.55)',// Aumentado de 0.5
    accentSolid: '#00d4aa',
    error: 'rgba(248, 81, 73, 0.5)',
    errorSolid: '#f85149',
    focus: '#00d4aa',
    divider: 'rgba(255, 255, 255, 0.1)',    // Aumentado de 0.06
  },

  // Shadows
  shadows: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.2)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.3)',
    md: '0 4px 12px rgba(0, 0, 0, 0.4)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.5)',
    xl: '0 16px 48px rgba(0, 0, 0, 0.6)',
    '2xl': '0 24px 64px rgba(0, 0, 0, 0.7)',
    inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    insetStrong: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)',
    // Glows para estados ativos
    glowAccent: '0 0 20px rgba(0, 212, 170, 0.3)',
    glowAccentStrong: '0 0 30px rgba(0, 212, 170, 0.5)',
    glowOnline: '0 0 12px rgba(0, 212, 170, 0.4)',
    glowMoving: '0 0 12px rgba(88, 166, 255, 0.4)',
    glowAlarm: '0 0 16px rgba(248, 81, 73, 0.4)',
    // Focus ring
    focusRing: '0 0 0 3px rgba(0, 212, 170, 0.3)',
    focusRingError: '0 0 0 3px rgba(248, 81, 73, 0.3)',
  },

  // Glassmorphism presets
  glassmorphism: {
    light: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
    },
    medium: {
      background: 'rgba(22, 27, 34, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    heavy: {
      // Opacidade 0.95 para cor mais consistente entre TopBar e Sidebar
      background: 'rgba(13, 17, 23, 0.95)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
    },
  },

  // ============================================================================
  // BUTTON TOKENS (Novo em v2.0)
  // ============================================================================
  button: {
    primary: {
      bg: '#00d4aa',
      bgHover: '#00e4ba',
      bgActive: '#00c49a',
      text: '#0d1117',
      border: 'transparent',
    },
    secondary: {
      bg: '#21262d',
      bgHover: '#30363d',
      bgActive: '#3c444d',
      text: '#c9d1d9',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    danger: {
      bg: '#da3633',
      bgHover: '#f85149',
      bgActive: '#b62324',
      text: '#ffffff',
      border: 'transparent',
    },
    ghost: {
      bg: 'transparent',
      bgHover: 'rgba(255, 255, 255, 0.06)',
      bgActive: 'rgba(255, 255, 255, 0.1)',
      text: '#8b949e',
      textHover: '#c9d1d9',
      border: 'transparent',
    },
    outline: {
      bg: 'transparent',
      bgHover: 'rgba(0, 212, 170, 0.1)',
      bgActive: 'rgba(0, 212, 170, 0.15)',
      text: '#00d4aa',
      border: 'rgba(0, 212, 170, 0.5)',
      borderHover: '#00d4aa',
    },
    link: {
      bg: 'transparent',
      bgHover: 'transparent',
      bgActive: 'transparent',
      text: '#00d4aa',
      textHover: '#00e4ba',
      border: 'transparent',
    },
    // Tamanhos
    sizes: {
      xs: { height: '24px', fontSize: '11px', padding: '0 8px', iconSize: '12px' },
      sm: { height: '28px', fontSize: '12px', padding: '0 10px', iconSize: '14px' },
      md: { height: '36px', fontSize: '14px', padding: '0 16px', iconSize: '16px' },
      lg: { height: '44px', fontSize: '16px', padding: '0 20px', iconSize: '18px' },
      xl: { height: '52px', fontSize: '18px', padding: '0 24px', iconSize: '20px' },
    },
  },

  // ============================================================================
  // CHARTS TOKENS (Novo em v2.0)
  // ============================================================================
  chart: {
    // Grid e eixos
    grid: 'rgba(255, 255, 255, 0.06)',
    gridStrong: 'rgba(255, 255, 255, 0.1)',
    axis: '#6e7681',
    axisLabel: '#8b949e',
    // Tooltip
    tooltip: {
      bg: '#21262d',
      bgBlur: 'rgba(33, 38, 45, 0.95)',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#f0f6fc',
      textSecondary: '#8b949e',
    },
    // Paleta de cores para séries
    series: [
      '#00d4aa', // Teal (primary)
      '#58a6ff', // Blue
      '#a371f7', // Purple
      '#d29922', // Amber
      '#f97316', // Orange
      '#f85149', // Red
      '#3fb950', // Green
      '#79c0ff', // Light blue
      '#bc8cff', // Light purple
      '#e3b341', // Yellow
    ],
    // Cores específicas por tipo
    positive: '#00d4aa',
    negative: '#f85149',
    neutral: '#8b949e',
    // Área/preenchimento
    areaOpacity: 0.1,
    areaOpacityHover: 0.2,
  },

  // ============================================================================
  // COMMAND BAR TOKENS (Novo em v2.0)
  // ============================================================================
  commandBar: {
    bg: '#161b22',
    bgBlur: 'rgba(22, 27, 34, 0.95)',
    border: 'rgba(255, 255, 255, 0.1)',
    shadow: '0 16px 70px rgba(0, 0, 0, 0.5)',
    // Input
    inputBg: 'rgba(255, 255, 255, 0.05)',
    inputBorder: 'rgba(255, 255, 255, 0.1)',
    inputText: '#f0f6fc',
    inputPlaceholder: '#6e7681',
    // Results
    resultBg: 'transparent',
    resultBgHover: 'rgba(255, 255, 255, 0.06)',
    resultBgSelected: 'rgba(0, 212, 170, 0.1)',
    resultText: '#f0f6fc',
    resultTextSecondary: '#8b949e',
    // Category headers
    categoryBg: 'transparent',
    categoryText: '#6e7681',
    // Shortcuts/badges
    shortcutBg: 'rgba(255, 255, 255, 0.1)',
    shortcutText: '#8b949e',
    shortcutBorder: 'rgba(255, 255, 255, 0.1)',
  },

  // ============================================================================
  // ACTIVITY FEED TOKENS (Novo em v2.0)
  // ============================================================================
  activityFeed: {
    // Timeline line
    lineBg: 'rgba(255, 255, 255, 0.1)',
    // Event dot
    dotBg: '#21262d',
    dotBorder: 'rgba(255, 255, 255, 0.2)',
    dotBorderActive: '#00d4aa',
    // Event card
    eventBg: 'transparent',
    eventBgHover: 'rgba(255, 255, 255, 0.04)',
    eventBorder: 'transparent',
    // Text
    eventTitle: '#f0f6fc',
    eventDescription: '#8b949e',
    eventTime: '#6e7681',
    // Day separator
    daySeparatorBg: 'rgba(255, 255, 255, 0.06)',
    daySeparatorText: '#8b949e',
  },

  // ============================================================================
  // CRUD SPECIFIC TOKENS
  // ============================================================================
  crud: {
    // Tabela
    table: {
      headerBg: '#161b22',
      headerText: '#8b949e',
      rowBg: 'transparent',
      rowBgAlt: 'rgba(255, 255, 255, 0.02)',
      rowHover: 'rgba(255, 255, 255, 0.04)',
      rowSelected: 'rgba(0, 212, 170, 0.08)',
      rowStripe: 'rgba(255, 255, 255, 0.02)',
      borderColor: 'rgba(255, 255, 255, 0.06)',
      // Pagination
      paginationBg: 'transparent',
      paginationText: '#8b949e',
      paginationActive: '#00d4aa',
      paginationActiveBg: 'rgba(0, 212, 170, 0.1)',
    },
    // Cards de listagem
    card: {
      bg: '#161b22',
      bgHover: '#1c2128',
      bgSelected: 'rgba(0, 212, 170, 0.08)',
      borderSelected: 'rgba(0, 212, 170, 0.5)',
      statBg: 'rgba(0, 212, 170, 0.08)',
    },
    // Formulários
    form: {
      sectionBg: '#161b22',
      sectionBorder: 'rgba(255, 255, 255, 0.06)',
      inputBg: '#0d1117',
      inputBgHover: '#161b22',
      inputBgFocus: '#0d1117',
      inputBorder: 'rgba(255, 255, 255, 0.1)',
      inputBorderHover: 'rgba(255, 255, 255, 0.2)',
      inputBorderFocus: '#00d4aa',
      inputBorderError: '#f85149',
      labelColor: '#8b949e',
      labelFocusColor: '#00d4aa',
      helperText: '#6e7681',
      errorText: '#f85149',
    },
    // Tabs
    tabs: {
      bg: 'transparent',
      borderBottom: 'rgba(255, 255, 255, 0.08)',
      tabColor: '#8b949e',
      tabHoverColor: '#f0f6fc',
      tabHoverBg: 'rgba(255, 255, 255, 0.04)',
      tabActiveColor: '#f0f6fc',
      indicatorColor: '#00d4aa',
      indicatorHeight: '2px',
    },
    // Actions buttons
    actions: {
      primaryBg: '#00d4aa',
      primaryText: '#0d1117',
      primaryHoverBg: '#00e4ba',
      secondaryBg: 'transparent',
      secondaryText: '#8b949e',
      secondaryBorder: 'rgba(255, 255, 255, 0.1)',
      secondaryHoverBg: 'rgba(255, 255, 255, 0.06)',
      dangerBg: '#da3633',
      dangerText: '#ffffff',
      dangerHoverBg: '#f85149',
    },
    // Empty state
    empty: {
      iconColor: '#6e7681',
      textColor: '#8b949e',
      bgColor: 'rgba(255, 255, 255, 0.02)',
    },
    // Filters
    filters: {
      chipBg: 'rgba(255, 255, 255, 0.06)',
      chipBgActive: 'rgba(0, 212, 170, 0.15)',
      chipText: '#8b949e',
      chipTextActive: '#00d4aa',
      chipBorder: 'rgba(255, 255, 255, 0.1)',
      chipBorderActive: 'rgba(0, 212, 170, 0.3)',
      dropdownBg: '#161b22',
      dropdownBorder: 'rgba(255, 255, 255, 0.1)',
    },
  },

  // ============================================================================
  // COMPONENT SPECIFIC TOKENS
  // ============================================================================
  components: {
    kpi: {
      bg: '#161b22',
      bgHover: '#1c2128',
      bgSelected: 'rgba(0, 212, 170, 0.08)',
      borderDefault: 'rgba(255, 255, 255, 0.06)',
      borderSelected: '#00d4aa',
      valueColor: '#f0f6fc',
      labelColor: '#8b949e',
      iconColor: '#6e7681',
    },
    search: {
      bg: '#0d1117',
      bgFocus: '#0d1117',
      border: 'rgba(255, 255, 255, 0.1)',
      borderFocus: '#00d4aa',
      iconColor: '#6e7681',
      placeholderColor: '#6e7681',
    },
    badge: {
      defaultBg: 'rgba(255, 255, 255, 0.1)',
      defaultText: '#8b949e',
    },
    tooltip: {
      bg: '#21262d',
      bgBlur: 'rgba(33, 38, 45, 0.95)',
      text: '#f0f6fc',
      border: 'rgba(255, 255, 255, 0.1)',
    },
    modal: {
      backdropBg: 'rgba(0, 0, 0, 0.6)',
      bg: '#161b22',
      headerBg: '#161b22',
      footerBg: '#0d1117',
      border: 'rgba(255, 255, 255, 0.1)',
      shadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
    },
    sidebar: {
      bg: '#0d1117',
      itemHover: 'rgba(255, 255, 255, 0.05)',
      itemActive: 'rgba(0, 212, 170, 0.1)',
      itemActiveText: '#00d4aa',
      divider: 'rgba(255, 255, 255, 0.06)',
    },
    select: {
      bg: '#0d1117',
      bgHover: '#161b22',
      menuBg: '#161b22',
      menuBorder: 'rgba(255, 255, 255, 0.1)',
      menuShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
      optionHover: 'rgba(255, 255, 255, 0.06)',
      optionSelected: 'rgba(0, 212, 170, 0.1)',
      optionSelectedText: '#00d4aa',
      chipBg: 'rgba(0, 212, 170, 0.15)',
      chipText: '#00d4aa',
    },
    toast: {
      bg: '#21262d',
      bgBlur: 'rgba(33, 38, 45, 0.95)',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#f0f6fc',
      shadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
      successAccent: '#00d4aa',
      errorAccent: '#f85149',
      warningAccent: '#d29922',
      infoAccent: '#58a6ff',
    },
    skeleton: {
      bg: '#21262d',
      shimmer: 'linear-gradient(90deg, #21262d 0%, #30363d 50%, #21262d 100%)',
    },
  },
};

// ============================================================================
// LIGHT MODE TOKENS
// ============================================================================

export const lightTokens = {
  // Accent colors (mais escuros para contraste em fundo claro)
  accent: {
    primary: '#00a884',
    primaryHover: '#00b894',
    primaryActive: '#009874',
    secondary: '#0099cc',
    tertiary: '#00b8d4',
    glow: 'rgba(0, 168, 132, 0.3)',
    subtle: 'rgba(0, 168, 132, 0.08)',
    hover: 'rgba(0, 168, 132, 0.12)',
    muted: 'rgba(0, 168, 132, 0.7)',
  },

  // Surfaces
  surface: {
    base: '#ffffff',
    elevated: '#f6f8fa',
    sunken: '#f0f3f6',
    overlay: 'rgba(255, 255, 255, 0.9)',
    overlayStrong: 'rgba(255, 255, 255, 0.95)',
    glass: 'rgba(255, 255, 255, 0.7)',
    glassHover: 'rgba(255, 255, 255, 0.85)',
    glassBorder: 'rgba(0, 0, 0, 0.08)',
    card: '#ffffff',
    cardHover: '#f6f8fa',
    cardActive: '#eaeef2',
    input: '#ffffff',
    inputHover: '#f6f8fa',
    inputFocus: '#ffffff',
    hover: '#f6f8fa',
    active: '#eaeef2',
    selected: 'rgba(0, 168, 132, 0.06)',
  },

  // Status colors - Padrão Traccar + Moving azul (customização 2ECOMP)
  status: {
    online: '#16a34a',            // Verde (padrão Traccar)
    offline: '#dc2626',           // Vermelho (padrão Traccar)
    moving: '#2563eb',            // Azul (customização 2ECOMP)
    idle: '#f59e0b',              // Âmbar - parado com ignição
    stopped: '#f59e0b',           // Âmbar (igual idle)
    alarm: '#dc2626',             // Vermelho alarme
    maintenance: '#8b5cf6',       // Roxo
    unknown: '#6b7280',           // Cinza (padrão Traccar)
    // Glows mais fortes (combinando com as cores acima)
    onlineGlow: 'rgba(22, 163, 74, 0.4)',
    offlineGlow: 'rgba(220, 38, 38, 0.4)',
    movingGlow: 'rgba(37, 99, 235, 0.4)',
    alarmGlow: 'rgba(220, 38, 38, 0.45)',
    // Backgrounds com opacidade para chips/badges
    onlineBg: 'rgba(22, 163, 74, 0.12)',
    offlineBg: 'rgba(220, 38, 38, 0.12)',
    movingBg: 'rgba(37, 99, 235, 0.12)',
    idleBg: 'rgba(245, 158, 11, 0.15)',
    stoppedBg: 'rgba(245, 158, 11, 0.15)',
    alarmBg: 'rgba(220, 38, 38, 0.12)',
    maintenanceBg: 'rgba(139, 92, 246, 0.12)',
  },

  // Trip/Dispatch Status
  tripStatus: {
    planned: '#656d76',
    scheduled: '#8250df',
    inProgress: '#0969da',
    loading: '#bf8700',
    unloading: '#9a6700',
    waitingToLoad: '#b08800',
    waitingToUnload: '#7d4e00',
    completed: '#1a7f37',
    delayed: '#bc4c00',
    cancelled: '#cf222e',
    onTime: '#00a884',
    early: '#0099cc',
    late: '#bc4c00',
    plannedBg: 'rgba(101, 109, 118, 0.12)',
    scheduledBg: 'rgba(130, 80, 223, 0.12)',
    inProgressBg: 'rgba(9, 105, 218, 0.12)',
    completedBg: 'rgba(26, 127, 55, 0.12)',
    delayedBg: 'rgba(188, 76, 0, 0.12)',
    cancelledBg: 'rgba(207, 34, 46, 0.12)',
  },

  // Semantic colors - Light Mode (CORES VIBRANTES para contraste)
  semantic: {
    success: '#00875a',           // Verde escuro para contraste
    successHover: '#00996b',
    error: '#cf222e',             // Vermelho forte
    errorHover: '#a40e26',
    warning: '#9a6700',           // Amarelo/laranja forte
    warningHover: '#7d4e00',
    info: '#0969da',              // Azul forte
    infoHover: '#0550ae',
    neutral: '#656d76',
    purple: '#8250df',
    // Backgrounds com opacidade MAIOR (0.15 ao invés de 0.1)
    successBg: 'rgba(0, 135, 90, 0.15)',    // Verde mais visível
    errorBg: 'rgba(207, 34, 46, 0.15)',     // Vermelho mais visível
    warningBg: 'rgba(154, 103, 0, 0.15)',   // Amarelo mais visível
    infoBg: 'rgba(9, 105, 218, 0.15)',      // Azul mais visível
    neutralBg: 'rgba(101, 109, 118, 0.12)',
    purpleBg: 'rgba(130, 80, 223, 0.12)',
  },

  // Text hierarchy
  text: {
    primary: '#1f2328',
    secondary: '#656d76',
    tertiary: '#8b949e',
    muted: '#8b949e',
    disabled: '#afb8c1',
    placeholder: '#8b949e',
    accent: '#00a884',
    inverse: '#ffffff',
    onAccent: '#ffffff', // Text on accent-colored backgrounds (buttons, etc.)
    link: '#0969da',
    linkHover: '#0550ae',
    error: '#cf222e',
    success: '#00a884',
    warning: '#bf8700',
  },

  // Borders
  border: {
    subtle: 'rgba(0, 0, 0, 0.04)',
    default: 'rgba(0, 0, 0, 0.1)',
    strong: 'rgba(0, 0, 0, 0.2)',
    accent: 'rgba(0, 168, 132, 0.3)',
    accentStrong: 'rgba(0, 168, 132, 0.5)',
    accentSolid: '#00a884',
    error: 'rgba(207, 34, 46, 0.5)',
    errorSolid: '#cf222e',
    focus: '#00a884',
    divider: 'rgba(0, 0, 0, 0.08)',
  },

  // Shadows (mais suaves para light mode)
  shadows: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
    md: '0 4px 12px rgba(0, 0, 0, 0.1)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
    xl: '0 16px 48px rgba(0, 0, 0, 0.15)',
    '2xl': '0 24px 64px rgba(0, 0, 0, 0.18)',
    inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    insetStrong: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
    glowAccent: '0 0 20px rgba(0, 168, 132, 0.2)',
    glowAccentStrong: '0 0 30px rgba(0, 168, 132, 0.3)',
    glowOnline: '0 0 12px rgba(0, 168, 132, 0.3)',
    glowMoving: '0 0 12px rgba(9, 105, 218, 0.3)',
    glowAlarm: '0 0 16px rgba(207, 34, 46, 0.3)',
    focusRing: '0 0 0 3px rgba(0, 168, 132, 0.2)',
    focusRingError: '0 0 0 3px rgba(207, 34, 46, 0.2)',
  },

  // Glassmorphism presets (adaptados para light mode)
  glassmorphism: {
    light: {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
    },
    medium: {
      background: 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(0, 0, 0, 0.08)',
    },
    heavy: {
      // Opacidade 0.95 para cor mais consistente entre TopBar e Sidebar
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 0, 0, 0.1)',
    },
  },

  // ============================================================================
  // BUTTON TOKENS - LIGHT MODE
  // ============================================================================
  button: {
    primary: {
      bg: '#00a884',
      bgHover: '#00b894',
      bgActive: '#009874',
      text: '#ffffff',
      border: 'transparent',
    },
    secondary: {
      bg: '#f6f8fa',
      bgHover: '#eaeef2',
      bgActive: '#dde2e7',
      text: '#1f2328',
      border: 'rgba(0, 0, 0, 0.1)',
    },
    danger: {
      bg: '#cf222e',
      bgHover: '#a40e26',
      bgActive: '#82071e',
      text: '#ffffff',
      border: 'transparent',
    },
    ghost: {
      bg: 'transparent',
      bgHover: 'rgba(0, 0, 0, 0.04)',
      bgActive: 'rgba(0, 0, 0, 0.08)',
      text: '#656d76',
      textHover: '#1f2328',
      border: 'transparent',
    },
    outline: {
      bg: 'transparent',
      bgHover: 'rgba(0, 168, 132, 0.08)',
      bgActive: 'rgba(0, 168, 132, 0.12)',
      text: '#00a884',
      border: 'rgba(0, 168, 132, 0.5)',
      borderHover: '#00a884',
    },
    link: {
      bg: 'transparent',
      bgHover: 'transparent',
      bgActive: 'transparent',
      text: '#00a884',
      textHover: '#00b894',
      border: 'transparent',
    },
    sizes: {
      xs: { height: '24px', fontSize: '11px', padding: '0 8px', iconSize: '12px' },
      sm: { height: '28px', fontSize: '12px', padding: '0 10px', iconSize: '14px' },
      md: { height: '36px', fontSize: '14px', padding: '0 16px', iconSize: '16px' },
      lg: { height: '44px', fontSize: '16px', padding: '0 20px', iconSize: '18px' },
      xl: { height: '52px', fontSize: '18px', padding: '0 24px', iconSize: '20px' },
    },
  },

  // ============================================================================
  // CHARTS TOKENS - LIGHT MODE
  // ============================================================================
  chart: {
    grid: 'rgba(0, 0, 0, 0.06)',
    gridStrong: 'rgba(0, 0, 0, 0.1)',
    axis: '#656d76',
    axisLabel: '#8b949e',
    tooltip: {
      bg: '#ffffff',
      bgBlur: 'rgba(255, 255, 255, 0.95)',
      border: 'rgba(0, 0, 0, 0.1)',
      text: '#1f2328',
      textSecondary: '#656d76',
    },
    series: [
      '#00a884',
      '#0969da',
      '#8250df',
      '#bf8700',
      '#bc4c00',
      '#cf222e',
      '#1a7f37',
      '#218bff',
      '#a475f9',
      '#b08800',
    ],
    positive: '#00a884',
    negative: '#cf222e',
    neutral: '#656d76',
    areaOpacity: 0.08,
    areaOpacityHover: 0.15,
  },

  // ============================================================================
  // COMMAND BAR TOKENS - LIGHT MODE
  // ============================================================================
  commandBar: {
    bg: '#ffffff',
    bgBlur: 'rgba(255, 255, 255, 0.95)',
    border: 'rgba(0, 0, 0, 0.1)',
    shadow: '0 16px 70px rgba(0, 0, 0, 0.15)',
    inputBg: 'rgba(0, 0, 0, 0.03)',
    inputBorder: 'rgba(0, 0, 0, 0.1)',
    inputText: '#1f2328',
    inputPlaceholder: '#8b949e',
    resultBg: 'transparent',
    resultBgHover: 'rgba(0, 0, 0, 0.04)',
    resultBgSelected: 'rgba(0, 168, 132, 0.08)',
    resultText: '#1f2328',
    resultTextSecondary: '#656d76',
    categoryBg: 'transparent',
    categoryText: '#8b949e',
    shortcutBg: 'rgba(0, 0, 0, 0.06)',
    shortcutText: '#656d76',
    shortcutBorder: 'rgba(0, 0, 0, 0.1)',
  },

  // ============================================================================
  // ACTIVITY FEED TOKENS - LIGHT MODE
  // ============================================================================
  activityFeed: {
    lineBg: 'rgba(0, 0, 0, 0.1)',
    dotBg: '#f6f8fa',
    dotBorder: 'rgba(0, 0, 0, 0.2)',
    dotBorderActive: '#00a884',
    eventBg: 'transparent',
    eventBgHover: 'rgba(0, 0, 0, 0.02)',
    eventBorder: 'transparent',
    eventTitle: '#1f2328',
    eventDescription: '#656d76',
    eventTime: '#8b949e',
    daySeparatorBg: 'rgba(0, 0, 0, 0.04)',
    daySeparatorText: '#656d76',
  },

  // ============================================================================
  // CRUD SPECIFIC TOKENS - LIGHT MODE
  // ============================================================================
  crud: {
    table: {
      headerBg: '#f6f8fa',
      headerText: '#656d76',
      rowBg: 'transparent',
      rowBgAlt: 'rgba(0, 0, 0, 0.01)',
      rowHover: 'rgba(0, 0, 0, 0.02)',
      rowSelected: 'rgba(0, 168, 132, 0.06)',
      rowStripe: 'rgba(0, 0, 0, 0.01)',
      borderColor: 'rgba(0, 0, 0, 0.08)',
      paginationBg: 'transparent',
      paginationText: '#656d76',
      paginationActive: '#00a884',
      paginationActiveBg: 'rgba(0, 168, 132, 0.08)',
    },
    card: {
      bg: '#ffffff',
      bgHover: '#f6f8fa',
      bgSelected: 'rgba(0, 168, 132, 0.06)',
      borderSelected: 'rgba(0, 168, 132, 0.5)',
      statBg: 'rgba(0, 168, 132, 0.06)',
    },
    form: {
      sectionBg: '#f6f8fa',
      sectionBorder: 'rgba(0, 0, 0, 0.08)',
      inputBg: '#ffffff',
      inputBgHover: '#f6f8fa',
      inputBgFocus: '#ffffff',
      inputBorder: 'rgba(0, 0, 0, 0.15)',
      inputBorderHover: 'rgba(0, 0, 0, 0.25)',
      inputBorderFocus: '#00a884',
      inputBorderError: '#cf222e',
      labelColor: '#656d76',
      labelFocusColor: '#00a884',
      helperText: '#8b949e',
      errorText: '#cf222e',
    },
    tabs: {
      bg: 'transparent',
      borderBottom: 'rgba(0, 0, 0, 0.1)',
      tabColor: '#656d76',
      tabHoverColor: '#1f2328',
      tabHoverBg: 'rgba(0, 0, 0, 0.03)',
      tabActiveColor: '#1f2328',
      indicatorColor: '#00a884',
      indicatorHeight: '2px',
    },
    actions: {
      primaryBg: '#00a884',
      primaryText: '#ffffff',
      primaryHoverBg: '#00b894',
      secondaryBg: 'transparent',
      secondaryText: '#656d76',
      secondaryBorder: 'rgba(0, 0, 0, 0.15)',
      secondaryHoverBg: 'rgba(0, 0, 0, 0.03)',
      dangerBg: '#cf222e',
      dangerText: '#ffffff',
      dangerHoverBg: '#a40e26',
    },
    empty: {
      iconColor: '#8b949e',
      textColor: '#656d76',
      bgColor: 'rgba(0, 0, 0, 0.02)',
    },
    filters: {
      chipBg: 'rgba(0, 0, 0, 0.04)',
      chipBgActive: 'rgba(0, 168, 132, 0.1)',
      chipText: '#656d76',
      chipTextActive: '#00a884',
      chipBorder: 'rgba(0, 0, 0, 0.1)',
      chipBorderActive: 'rgba(0, 168, 132, 0.3)',
      dropdownBg: '#ffffff',
      dropdownBorder: 'rgba(0, 0, 0, 0.1)',
    },
  },

  // ============================================================================
  // COMPONENT SPECIFIC TOKENS - LIGHT MODE
  // ============================================================================
  components: {
    kpi: {
      bg: '#ffffff',
      bgHover: '#f6f8fa',
      bgSelected: 'rgba(0, 168, 132, 0.06)',
      borderDefault: 'rgba(0, 0, 0, 0.08)',
      borderSelected: '#00a884',
      valueColor: '#1f2328',
      labelColor: '#656d76',
      iconColor: '#8b949e',
    },
    search: {
      bg: '#ffffff',
      bgFocus: '#ffffff',
      border: 'rgba(0, 0, 0, 0.15)',
      borderFocus: '#00a884',
      iconColor: '#8b949e',
      placeholderColor: '#8b949e',
    },
    badge: {
      defaultBg: 'rgba(0, 0, 0, 0.08)',
      defaultText: '#656d76',
    },
    tooltip: {
      bg: '#1f2328',
      bgBlur: 'rgba(31, 35, 40, 0.95)',
      text: '#ffffff',
      border: 'transparent',
    },
    modal: {
      backdropBg: 'rgba(0, 0, 0, 0.4)',
      bg: '#ffffff',
      headerBg: '#ffffff',
      footerBg: '#f6f8fa',
      border: 'rgba(0, 0, 0, 0.1)',
      shadow: '0 24px 64px rgba(0, 0, 0, 0.2)',
    },
    sidebar: {
      bg: '#ffffff',
      itemHover: 'rgba(0, 0, 0, 0.03)',
      itemActive: 'rgba(0, 168, 132, 0.08)',
      itemActiveText: '#00a884',
      divider: 'rgba(0, 0, 0, 0.08)',
    },
    select: {
      bg: '#ffffff',
      bgHover: '#f6f8fa',
      menuBg: '#ffffff',
      menuBorder: 'rgba(0, 0, 0, 0.1)',
      menuShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      optionHover: 'rgba(0, 0, 0, 0.04)',
      optionSelected: 'rgba(0, 168, 132, 0.08)',
      optionSelectedText: '#00a884',
      chipBg: 'rgba(0, 168, 132, 0.1)',
      chipText: '#00a884',
    },
    toast: {
      bg: '#ffffff',
      bgBlur: 'rgba(255, 255, 255, 0.95)',
      border: 'rgba(0, 0, 0, 0.1)',
      text: '#1f2328',
      shadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      successAccent: '#00a884',
      errorAccent: '#cf222e',
      warningAccent: '#bf8700',
      infoAccent: '#0969da',
    },
    skeleton: {
      bg: '#eaeef2',
      shimmer: 'linear-gradient(90deg, #eaeef2 0%, #f6f8fa 50%, #eaeef2 100%)',
    },
  },
};

// ============================================================================
// BACKWARDS COMPATIBILITY - Default export (dark mode)
// Para componentes que ainda usam import direto
// ============================================================================

// Re-export dark tokens as named exports for backwards compatibility
export const accent = darkTokens.accent;
export const surface = darkTokens.surface;
export const status = darkTokens.status;
export const tripStatus = darkTokens.tripStatus;
export const semantic = darkTokens.semantic;
export const text = darkTokens.text;
export const border = darkTokens.border;
export const shadows = darkTokens.shadows;
export const glassmorphism = darkTokens.glassmorphism;
export const button = darkTokens.button;
export const chart = darkTokens.chart;
export const commandBar = darkTokens.commandBar;
export const activityFeed = darkTokens.activityFeed;
export const crud = darkTokens.crud;
export const components = darkTokens.components;

// Default export - dark tokens + shared tokens
const premiumTokens = {
  ...darkTokens,
  typography,
  spacing,
  radius,
  transitions,
  zIndex,
  breakpoints,
};

export default premiumTokens;
